import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import supabase from '../supabase/supabase';
import { FiCalendar, FiUser, FiArrowLeft } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .eq('published', true)
      .maybeSingle();

    if (error || !data) {
      setPost(null);
    } else {
      setPost(data);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-28 md:pt-36 lg:pt-24">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-brand-lightText font-serif italic">Loading article...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-28 md:pt-36 lg:pt-24">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <h1 className="text-4xl font-serif text-brand-darkSage mb-4">Article Not Found</h1>
            <p className="text-brand-lightText mb-8">The article you're looking for doesn't exist or has been removed.</p>
            <Link to="/blog" className="bg-brand-darkSage text-white px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-all">
              Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-28 md:pt-36 lg:pt-24">
      <Navbar />
      <div className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-12 w-full">

        {/* Back Link */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-brand-lightText hover:text-brand-gold transition-colors mb-6 md:mb-8">
          <SafeIcon icon={FiArrowLeft} size={14} /> Back to Blog
        </Link>

        {/* Hero Image */}
        {post.cover_image && (
          <div className="rounded-2xl sm:rounded-3xl overflow-hidden mb-8 md:mb-10 shadow-xl">
            <img src={post.cover_image} alt={post.title} className="w-full h-52 sm:h-64 md:h-96 object-cover" />
          </div>
        )}

        {/* Article Header */}
        <div className="mb-8 md:mb-10">
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-serif text-brand-darkSage leading-tight mb-4 md:mb-6">
            {post.title}
          </h1>
          <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-brand-lightText">
            <span className="flex items-center gap-1.5">
              <SafeIcon icon={FiCalendar} size={14} />
              {new Date(post.published_at || post.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="text-brand-darkSage/20">|</span>
            <span className="flex items-center gap-1.5">
              <SafeIcon icon={FiUser} size={14} />
              {post.author}
            </span>
          </div>
        </div>

        {/* Article Content */}
        <div
          className="prose prose-lg max-w-none blog-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Bottom CTA */}
        <div className="mt-12 md:mt-16 pt-8 md:pt-10 border-t border-brand-darkSage/10 text-center">
          <p className="text-brand-lightText mb-4 font-serif italic">Enjoyed this article?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link to="/blog" className="w-full sm:w-auto text-center bg-brand-darkSage text-white px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-all">
              Read More Articles
            </Link>
            <Link to="/shop" className="w-full sm:w-auto text-center border-2 border-brand-darkSage text-brand-darkSage px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-brand-darkSage hover:text-white transition-all">
              Shop Now
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BlogPost;